import axiosIns from '@/libs/axios'
import DriverCognito from '@/libs/driver-cognito'

export default {
  namespaced: true,
  state: {
    drivers: [],
    currentDriver: null,
  },
  getters: {
    drivers: state => state.drivers,
    currentDriver: state => state.currentDriver,
  },
  mutations: {
    SET_DRIVERS: (state, drivers) => { state.drivers = drivers },
    SET_CURRENT_DRIVER: (state, driver) => { state.currentDriver = driver },
  },
  actions: {
    getDriverCarCount(_, carId) {
      return axiosIns.get(`drivers/cars/${carId}`)
    },
    replacePhoneNumber(_, { driverId, newDriverId, actorId }) {
      return axiosIns.patch(`drivers/${driverId}/replace-phone-number`, { newDriverId, actorId })
    },
    async fetchDriversLocation(_, params) {
      return axiosIns.get('dispatch/drivers-location', {
        params,
      })
    },
    fetchDriverByExternalId(_, externalId) {
      return axiosIns.get(`drivers/externals/${externalId}`)
    },
    getProfilePicPresignedURL(_, driverId) {
      return axiosIns.post(`drivers/${driverId}/picture`)
    },
    getProfilePicture(_, driverId) {
      return axiosIns.get(`drivers/${driverId}/picture`)
    },
    fetchUnverifiedDrivers(_, params) {
      return axiosIns.get('/drivers/unverified', {
        params,
      })
    },
    async fetchDrivers({ commit }, queryParams) {
      const response = (await axiosIns.get('/drivers', { params: queryParams, timeout: 3000 })).data.data
      commit('SET_DRIVERS', response)
      return response
    },
    async createDriver(ctx, { phoneNumber, passcode }) {
      return new Promise((resolve, reject) => {
        const driverCognito = new DriverCognito()
        driverCognito.register(phoneNumber, passcode).then(driver => {
          resolve(driver)
        }).catch(error => {
          reject(error)
        })
      })
    },
    async verifyPhoneNumber(ctx, { username, code }) {
      return new Promise((resolve, reject) => {
        const driverCognito = new DriverCognito()
        driverCognito.verifyPhoneNumber(username, code).then(result => {
          resolve(result)
        }).catch(error => {
          reject(error)
        })
      })
    },
    resendConfirmationCode(ctx, username) {
      const driverCognito = new DriverCognito()
      return driverCognito.resendConfirmationCode(username)
    },
    async fetchDriver({ commit }, { id }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`/drivers/${id}`)
          .then(response => {
            commit('SET_CURRENT_DRIVER', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchDriverByAuthId({ commit }, { authId }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`/drivers/auth/${authId}`)
          .then(response => {
            commit('SET_CURRENT_DRIVER', response.data.data)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    fetchDriverByCarNumber({ commit }, { carNumber }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`/cars/${carNumber}/car-number/`)
          .then(response => {
            commit('SET_CURRENT_DRIVER', response.data.data)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    updateDriverCar(ctx, { carId, data }) {
      return axiosIns.patch(`drivers/${data.driverId}`, { carId })
    },
    updateDriver({ commit }, { id, data, addedByAdmin }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .patch(`/drivers/${id}`, {
            ...data,
            addedByAdmin,
          })
          .then(response => {
            commit('SET_CURRENT_DRIVER', response.data.data)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    addCar({ dispatch }, { data }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post('/cars/', data)
          .then(async response => {
            await dispatch('fetchDriver', { id: data.driverId })
            resolve(response.data)
          })
          .catch(error => reject(error))
      })
    },
    updateCar(ctx, { carId, data }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .patch(`/cars/${carId}`, data)
          .then(async response => {
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    fetchCar(ctx, { carId }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`/cars/${carId}`)
          .then(response => resolve(response.data.data))
          .catch(error => reject(error))
      })
    },
    fetchCarDetailsByCarNumbr(ctx, { carNumber }) {
      return axiosIns.get(`/cars/${carNumber}/car-number`)
    },
    addService(ctx, { driverId, service }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`/drivers/${driverId}/services`, {
            service: service.service,
            class: service.class,
            metadata: {
              open_for_all_zones: true,
              constrained_zones: [],
            },
          })
          .then(response => resolve(response.data.data))
          .catch(error => reject(error))
      })
    },
    removeService(ctx, { driverId, serviceId }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .delete(`/drivers/${driverId}/services/${serviceId}`)
          .then(response => resolve(response.data.data))
          .catch(error => reject(error))
      })
    },
    updateService(ctx, { driverId, service }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .patch(`/drivers/${driverId}/services/${service.id}`, { metadata: service.metadata })
          .then(response => resolve(response.data.data))
          .catch(error => reject(error))
      })
    },
    getService(_, { driverId }) {
      return axiosIns.get(`/drivers/${driverId}/services/`)
    },
    fetchDriverDetails(_, id) {
      return axiosIns.get(`/drivers/externals/${id}`)
    },
    // eslint-disable-next-line camelcase
    fetchLegacyNotes(_, external_id) {
      return axiosIns.get('/drivers/notes/legacy', {
        params: {
          external_id,
        },
      })
    },
    fetchStatsAggs(_, { serviceType }) {
      return axiosIns.get('/drivers/stats/aggs', {
        params: {
          service_type: serviceType,
        },
      })
    },
    fetchLastOtp(_, phone) {
      return axiosIns.get('users/last-otp', {
        params: {
          phone,
        },
      })
    },
    // eslint-disable-next-line camelcase
    fetchDriverLocation(_, external_id) {
      // eslint-disable-next-line camelcase
      return axiosIns.get(`/drivers/location/single/${external_id}`)
    },
    fetchRegistrationReports(_, params) {
      return axiosIns.get('/drivers/driver/registrated', {
        params,
      })
    },
    fetchViolationTypes() {
      return axiosIns.get('/drivers/violations/types')
    },
    fetchViolationsList(_, params) {
      return axiosIns.get('/drivers/violations', {
        params,
      })
    },
    createDriverViolation(_, payload) {
      return axiosIns.post('/drivers/violations/drivers', payload)
    },
    fetchViolationTriggers(_, { violationId }) {
      return axiosIns.get(`/drivers/violations/triggers/${violationId}`)
    },
    applyViolationTrigger(_, { violationId, body }) {
      return axiosIns.post(`/drivers/violations/triggers/${violationId}`, body)
    },
    fetchDriverViolationAggs(_, { driverID }) {
      return axiosIns.get(`/drivers/violations/aggs/${driverID}`)
    },
    fetchLearnings() {
      return axiosIns.get('/drivers/learnings')
    },
    createLearning(_, payload) {
      return axiosIns.post('/drivers/learnings', payload)
    },
    getLearning(_, { learningId, params }) {
      return axiosIns.get(`/drivers/learnings/${learningId}`, {
        params,
      })
    },
    updateLearning(_, { learningId, payload }) {
      return axiosIns.patch(`/drivers/learnings/${learningId}`, payload)
    },
    deleteLearning(_, learningId) {
      return axiosIns.delete(`/drivers/learnings/${learningId}`)
    },
  },
}
